import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Icon from '../../../Icon/Icon';
import {
    formatNumber,
    getCampaignDurationByPeriodValue,
    formatDateAccordingToBrowser,
    numberWithoutCommas,
} from '../../../../utils/functions';
import { useTranslation } from 'react-i18next';
import Block from './Block';
import Item from './Item';

// The wizard keeps the basics in the CreateCampaignPage state (the same object
// used to save and launch), so the review has to read them from there. Reading
// them from the newCampaign slice showed the values the campaign had when it
// was loaded, not the ones the user had just set.
const Basics = ({ basics }) => {
    const { t } = useTranslation();
    const selectedCurrency = basics?.selectedCurrency;
    const budget = basics?.budget?.budget;
    const periodValue = basics?.timing?.periodValue;
    const dailyCaps = basics?.dailyCaps;
    const campaignDuration = getCampaignDurationByPeriodValue(periodValue);
    const validCampaignDuration =
        campaignDuration !== -1 && campaignDuration !== 0;
    const hasBudget = budget !== '' && budget !== 0 && budget != null;

    const assignedCapsTotal = useMemo(() => {
        const caps = dailyCaps?.caps ?? {};
        return Object.values(caps).reduce(
            (sum, value) => sum + (Number(numberWithoutCommas(value)) || 0),
            0
        );
    }, [dailyCaps]);

    const isDailyCapActive = Boolean(dailyCaps?.isActive) && assignedCapsTotal > 0;

    return (
        <Block
            icon={
                <Icon
                    name="pen"
                    fixedWidth
                    size="lg"
                    className="m-1 text-blue-300"
                />
            }
            title={t('sections.campaigns.new-campaign.wizard-tab.basics')}>
            <Item
                title={`${t(
                    'sections.campaigns.new-campaign.wizard-review.blocks.basics.daily-cap'
                )} ${
                    isDailyCapActive
                        ? t(
                              'sections.campaigns.new-campaign.wizard-review.blocks.basics.budget'
                          )
                        : ''
                }`}>
                <span>
                    {isDailyCapActive ? (
                        <>
                            <span className="text-xs font-light">
                                {selectedCurrency?.symbol ?? ''}
                            </span>{' '}
                            {formatNumber(assignedCapsTotal, 'currency')}
                        </>
                    ) : (
                        t(
                            'sections.campaigns.new-campaign.wizard-briefBar.basics.unlimited'
                        )
                    )}
                </span>
            </Item>

            <Item
                title={t(
                    'sections.campaigns.new-campaign.wizard-review.blocks.basics.goal-budget'
                )}>
                <span>
                    {
                        <span className="text-xs font-light">
                            {hasBudget ? (selectedCurrency?.symbol ?? '') : ''}
                        </span>
                    }{' '}
                    {hasBudget ? formatNumber(budget, 'currency') : '-'}
                </span>
            </Item>

            <Item
                title={t(
                    'sections.campaigns.new-campaign.wizard-review.blocks.basics.campaign-duration'
                )}>
                {`${validCampaignDuration ? campaignDuration : '-'} ${
                    validCampaignDuration ? t('common.words.days') : ''
                }`}
            </Item>

            <Item
                title={t(
                    'sections.campaigns.new-campaign.wizard-review.blocks.basics.period'
                )}>
                {`${
                    validCampaignDuration
                        ? `${formatDateAccordingToBrowser(
                              periodValue[0]
                          )} ~ ${formatDateAccordingToBrowser(periodValue[1])}`
                        : '-'
                }`}
            </Item>
        </Block>
    );
};

Basics.propTypes = {
    basics: PropTypes.shape({
        selectedCurrency: PropTypes.object,
        budget: PropTypes.object,
        timing: PropTypes.object,
        dailyCaps: PropTypes.object,
    }),
};

export default Basics;
